import { Layout } from "@/components/layout/Layout";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Mail, Instagram, MessageCircle, BadgeCheck,
  Shield, Clock, MapPin, ArrowRight, Youtube
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Contact() {
  return (
    <Layout>
      <Helmet>
        <title>Contact Us — Transparency Badge | Website Safety Checker</title>
        <meta name="description" content="Have a question about our website safety checker or the Verified Badge Program? Get in touch — we respond to every message within 1-2 business days." />
        <link rel="canonical" href="https://transparencybadge.com/contact" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container max-w-2xl mx-auto text-center space-y-4 animate-fade-in">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
            <MessageCircle className="h-3.5 w-3.5" />
            Get in Touch
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Questions? We're Here to Help
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Whether it's about the website checker, the Verified Badge, billing, or anything else — reach out and we'll get back to you.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 md:py-20">
        <div className="container max-w-lg mx-auto space-y-5">

          <div className="bg-safe-bg border border-safe/30 rounded-2xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-safe/20 flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-safe" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm">Response Time</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                We respond to all messages within{" "}
                <span className="font-medium text-foreground">1–2 business days</span>.
              </p>
            </div>
          </div>

          <div className="bg-card border rounded-2xl divide-y overflow-hidden shadow-soft">

            <div className="p-5 flex items-center gap-4 hover:bg-accent/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Email</p>
                <a
                  href="mailto:hello@transparencybadge.com"
                  className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors break-all"
                >
                  hello@transparencybadge.com
                </a>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            <div className="p-5 flex items-center gap-4 hover:bg-accent/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Instagram className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Instagram</p>
                <a
                  href="https://www.instagram.com/transparencybadge/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors"
                >
                  @transparencybadge
                </a>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            <div className="p-5 flex items-center gap-4 hover:bg-accent/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-primary" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">X (Twitter)</p>
                <a
                  href="https://x.com/trustbeforecliq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors"
                >
                  @trustbeforecliq
                </a>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            <div className="p-5 flex items-center gap-4 hover:bg-accent/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-primary" aria-hidden="true">
                  <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.781 3.631 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.36-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.74-1.757-.502-.586-1.28-.883-2.31-.89h-.029c-.825 0-1.945.227-2.661 1.29l-1.71-1.149c.96-1.43 2.52-2.217 4.37-2.217h.043c3.094.02 4.937 1.92 5.12 5.226.105.044.211.092.314.142 1.469.69 2.544 1.737 3.107 3.026.786 1.797.857 4.726-1.527 7.058-1.821 1.781-4.034 2.585-7.181 2.607Zm1.062-11.165c-.252 0-.504.008-.752.025-1.834.103-2.974.94-2.91 2.13.067 1.247 1.443 1.828 2.769 1.756 1.221-.067 2.815-.539 3.082-3.681a10.97 10.97 0 0 0-2.189-.23z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Threads</p>
                <a
                  href="https://www.threads.net/@transparencybadge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors"
                >
                  @transparencybadge
                </a>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            <div className="p-5 flex items-center gap-4 hover:bg-accent/30 transition-colors group">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Youtube className="h-5 w-5 text-primary" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">YouTube</p>
                <a
                  href="https://www.youtube.com/@transparencybadge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors"
                >
                  @transparencybadge
                </a>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>

            <div className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-0.5">Location</p>
                <p className="text-sm font-semibold text-foreground">Hyderabad, India</p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 border-t bg-muted/30">
        <div className="container max-w-lg mx-auto">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-card border rounded-xl p-6 space-y-3">
              <BadgeCheck className="h-6 w-6 text-primary" />
              <h3 className="font-semibold text-foreground">Get Your Site Verified</h3>
              <p className="text-sm text-muted-foreground">Apply for the Verified Badge. First month free, then $10/month.</p>
              <Button asChild size="sm" className="w-full">
                <Link to="/get-verified">Apply Now →</Link>
              </Button>
            </div>
            <div className="bg-card border rounded-xl p-6 space-y-3">
              <Shield className="h-6 w-6 text-primary" />
              <h3 className="font-semibold text-foreground">Check Any Website</h3>
              <p className="text-sm text-muted-foreground">Use our free safety checker — no signup, no cost.</p>
              <Button asChild size="sm" className="btn-orange w-full">
                <Link to="/">Check a Website →</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
}
